import React from 'react';
import { ContentArea } from '@styles/common';
import { TourneyTable } from '@screens/tourneys/tourneys.styles';
import { GridCellParams, GridColTypeDef, GridColumns } from '@material-ui/data-grid';
import { useAuth } from '@domain/auth/auth.hook';
import { CancelToken } from 'axios';
import { IconButton } from '@material-ui/core';
import { Link } from '@components/link';
import { Visibility } from '@material-ui/icons';
import { formatColWithDate } from '@components/datagrid/custom.datagrid.props';
import CustomGridCellMoneyIcon from '@components/datagrid/custom.grid.cell.money.icon';
import { noopArray } from '@domain/utils/function.utils';
import { MainDataGrid } from '@components/datagrid/main.data.grid';
import { usePagedData } from '@domain/hooks/use.paged.data';
import { GameType } from '@domain/game.selection/types/game.type';
import { useGame } from '@domain/game.selection/game.selection.hook';

const baseColProps: GridColTypeDef = {
  align: 'center',
  headerAlign: 'center',
};
const commonStartColumn: GridColumns = [{ field: 'tourneyId', headerName: 'Tourney Id', type: 'string', width: 143 }];
const commonEndColumn: GridColumns = [
  { field: 'pool', headerName: 'Pool', type: 'number', width: 105, ...baseColProps },
  {
    field: 'id',
    headerName: ' ',
    width: 50,
    renderCell: (params: GridCellParams) => (
      <IconButton color='primary' size='small' component={Link} to={`/tourneys/${params.value}`}>
        <Visibility />
      </IconButton>
    ),
    ...baseColProps,
  },
];

const columns: GridColumns = [
  ...commonStartColumn,
  { field: 'gameId', headerName: 'Game', type: 'string', width: 114 },
  { field: 'type', headerName: 'Type', type: 'string', width: 107 },
  { field: 'startDate', headerName: 'Start', ...formatColWithDate },
  { field: 'endDate', headerName: 'End', ...formatColWithDate },
  { field: 'durationInMinutes', headerName: 'Duration', type: 'number', width: 131, ...baseColProps },
  {
    field: 'cashPrizes',
    headerName: 'Cash',
    type: 'number',
    width: 109,
    renderCell: CustomGridCellMoneyIcon,
    ...baseColProps,
  },
  { field: 'geography', headerName: 'Geography', type: 'string', width: 146, ...baseColProps },
  ...commonEndColumn,
];

const rexColumns: GridColumns = [
  ...commonStartColumn,
  { field: 'tourneyScheduleId', headerName: 'Schedule Id', width: 151 },
  { field: 'scheduledStartDate', headerName: 'Scheduled Start', ...formatColWithDate, width: 179 },
  { field: 'scheduledCoolingDate', headerName: 'Scheduled Cooling', ...formatColWithDate, width: 196 },
  { field: 'scheduledClosureDate', headerName: 'Scheduled Closure', ...formatColWithDate, width: 196 },
  { field: 'startDate', headerName: 'Start', ...formatColWithDate },
  { field: 'coolingDate', headerName: 'Cooling', ...formatColWithDate },
  { field: 'closureDate', headerName: 'Closure', ...formatColWithDate },
  ...commonEndColumn,
];

interface Tourney {
  tourneyId: number;
}

function createData(tourney: Tourney) {
  return { id: tourney.tourneyId, ...tourney };
}

const Tourneys = () => {
  const game = useGame();
  const { authorizedServices } = useAuth();

  async function runSearch(data: any, page: number, cancelToken: CancelToken | undefined) {
    return authorizedServices.get<Tourney[]>('/admin/tourney/list', { params: { ...data, page }, cancelToken });
  }

  const { responseData, pageCount, loading, error } = usePagedData(null, runSearch, { placeholderData: noopArray });

  const rows = responseData?.map(createData) || [];

  return (
    <ContentArea>
      <TourneyTable>
        <MainDataGrid
          columns={game.type === GameType.Praia ? columns : rexColumns}
          rows={rows}
          loading={loading}
          error={error}
          pageCount={pageCount}
        />
      </TourneyTable>
    </ContentArea>
  );
};

export default Tourneys;
